import { API, Auth, Storage } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";
import { BookkeepingFull, TaxRate } from "../models";
import { dateToApiString } from "../utils";

// Formats the OCR Lambda accepts (Bedrock Converse): pdf document or jpeg/png/gif/webp image
export const receiptFileExtensions = ["pdf", "jpg", "jpeg", "png", "webp", "gif"];

export const isSupportedReceiptFile = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return !!extension && receiptFileExtensions.includes(extension);
};

// HEIC is the iPhone default, so it is the unsupported format users actually
// hit — worth a message that points somewhere instead of a blank "not
// supported". Bedrock takes no HEIC and browsers can't decode it without a wasm
// blob (86carq1wp); the app converts to jpeg on-device, hence the pointer to it.
export const isHeicFile = (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  return extension === "heic" || extension === "heif";
};

// Same mapping as the app: extension → Bedrock image format / pdf
const getOcrFormat = (extension: string) => {
  switch (extension) {
    case "pdf":
      return "pdf";
    case "png":
      return "png";
    case "webp":
      return "webp";
    case "gif":
      return "gif";
    default:
      return "jpeg";
  }
};

export type OcrTax = {
  rate: number | null;
  amount: number | null;
  totalAmount: number | null;
};

export type OcrResult = {
  date: string | null;
  amount: number | null;
  currencyCode: string | null;
  taxes: OcrTax[] | null;
  vendor: string | null;
};

// Key convention matches the app (uploadReceiptToS3): "public/<uuid>.<ext>",
// Attachment.key stores the bare fileId (fetchReceipt resolves via prefix list).
export const uploadReceiptToS3 = async (
  file: File
): Promise<{ fileId: string; s3Key: string }> => {
  const fileId = uuidv4();
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "pdf";
  const key = `${fileId}.${extension}`;
  await Storage.put(key, file, {
    level: "public",
    contentType: file.type || undefined,
  });
  return { fileId, s3Key: `public/${key}` };
};

export const performOcr = async (
  s3Key: string,
  extension: string
): Promise<OcrResult> => {
  const response: OcrResult = await API.post("expenslyREST", "/ocr", {
    headers: {
      Authorization: `Bearer ${(await Auth.currentSession())
        .getIdToken()
        .getJwtToken()}`,
    },
    body: {
      s3Key,
      format: getOcrFormat(extension),
    },
  });
  return response;
};

// Active-window check on AWSDate strings (YYYY-MM-DD sorts lexicographically).
// Parsing dateFrom via new Date() would yield UTC midnight vs. the local
// midnight of paidAt, excluding rates on their exact start day east of UTC.
export const isTaxRateActiveOn = (taxRate: TaxRate, paidAt: Date): boolean => {
  const paidAtString = dateToApiString(paidAt);
  return (
    !taxRate.deletedAt &&
    taxRate.dateFrom <= paidAtString &&
    (!taxRate.dateTo || taxRate.dateTo >= paidAtString)
  );
};

// Mirrors app getTaxRates (globals.dart): rates active on the date AND matching
// the booking account's taxType. The same rate exists once per taxType (2.6%
// Material vs 2.6% Investition) — without the taxType filter the list shows
// duplicates and a match picks an arbitrary one, booking the wrong VAT type.
export const getTaxRatesFor = (
  bookkeeping: BookkeepingFull,
  paidAt: Date,
  bookingAccountId: string | undefined
): TaxRate[] => {
  const taxType = bookingAccountId
    ? bookkeeping.bookingAccounts.find(
        (bookingAccount) => bookingAccount.id === bookingAccountId
      )?.taxType
    : undefined;
  return (bookkeeping.taxRates ?? []).filter(
    (taxRate) =>
      isTaxRateActiveOn(taxRate, paidAt) &&
      (taxType === undefined || taxRate.taxType === taxType)
  );
};

// Mirrors app getTaxAmount (globals.dart): the VAT is extracted from the gross,
// never entered. Single tax has no totalAmount, so the transaction total is the
// gross (same fallback as the app).
// Distinct from utils.ts getTaxAmount, which displays a stored Tax and derives
// only as a fallback — this one computes the value to store.
export const deriveTaxAmount = (
  transactionAmount: number,
  rate: number,
  totalAmount: number | null
): number =>
  Number(
    (
      ((totalAmount ?? transactionAmount) * (rate / 100)) /
      (rate / 100 + 1)
    ).toFixed(2)
  );

// Mirrors app ExpenslyApiService.performOCR tax matching: only rates that exist
// as active TaxRates for the paidAt date and the booking account's taxType,
// deduplicated per TaxRate.
//
// The OCR amount is KEPT: it is the VAT the supplier printed on the Beleg, and
// Vorsteuer is deducted per the invoice — the books have to reconcile to the
// document in an ESTV audit. Recomputing it from the total disagrees with Swiss
// receipts routinely (per-line rounding: a FAIRTIQ total of 35.80 at 8.1% prints
// 2.70, rausgerechnet gives 2.68). It is only replaced once the user edits
// something the value depends on — see taxDisplay/handleSave in the modal.
export const matchOcrTaxesToTaxRates = (
  ocrTaxes: OcrTax[] | null,
  bookkeeping: BookkeepingFull,
  paidAt: Date,
  bookingAccountId: string | undefined
): { taxRate: TaxRate; amount: number | null; totalAmount: number | null }[] => {
  if (!ocrTaxes) return [];

  const taxRatesActive = getTaxRatesFor(bookkeeping, paidAt, bookingAccountId);

  const matched: {
    taxRate: TaxRate;
    amount: number | null;
    totalAmount: number | null;
  }[] = [];
  for (const ocrTax of ocrTaxes) {
    if (ocrTax.rate === null) continue;
    const taxRate = taxRatesActive.find((rate) => rate.rate === ocrTax.rate);
    if (!taxRate) continue;
    if (matched.some((m) => m.taxRate.id === taxRate.id)) continue;
    matched.push({
      taxRate,
      amount: ocrTax.amount,
      totalAmount: ocrTax.totalAmount,
    });
  }
  // App supports exactly one or two rates (multipleTaxes / taxes[1]).
  return matched.slice(0, 2);
};
